import { useContext, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { RefetchTranscriptContext, TranscriptIdContext, TranscriptMetadataContext } from './context';
import type { Transcript } from './interfaces';

type Props = ChildrenProps;

export const TranscriptMetadataContainer = ({ children }: Props) => {
  const transcriptId = useContext(TranscriptIdContext);
  const refetch = useContext(RefetchTranscriptContext);

  const query = useTranscriptMetadata(transcriptId);

  const apiClient = useChatApiClient();

  const updateMetadataMutation = useMutation({
    mutationKey: [
      `patch:transcripts/metadata`,
      transcriptId,
    ],
    mutationFn: (params: Transcript.Metadata.Data) => {
      return apiClient.transcripts.updateMetadata({
        transcriptId,
        publishedOn: params.publishedOn,
        title: params.title,
      })
        .then(() => Promise.all([
          query.refetch(),
          refetch(),
        ]));
    },
  });

  const value = useMemo(() => ({
    title: query.data?.metadata?.title,
    publishedOn: query.data?.metadata?.publishedOn,
    updateMetadata: updateMetadataMutation.mutateAsync,
    isInitialLoading: query.isLoading || query.isPlaceholderData || !query.data,
    refetch: query.refetch,
    isMutating: updateMetadataMutation.isPending,
  }), [
    query.data,
    query.isLoading,
    query.isPlaceholderData,
    query.refetch,
    updateMetadataMutation,
  ]);

  return (
    <TranscriptMetadataContext.Provider value={value}>
      {children}
    </TranscriptMetadataContext.Provider>
  );
};

const useTranscriptMetadata = (transcriptId: number) => {
  const apiClient = useChatApiClient();

  return useQuery({
    queryKey: [
      `get:transcripts/metadata`,
      transcriptId,
    ],
    queryFn: () => apiClient.transcripts.getMetadata({ transcriptId }),
    placeholderData: {
      metadata: {
        publishedOn: null,
        title: null,
      },
    },
    refetchOnWindowFocus: false,
    enabled: !!transcriptId,
  });
};